import { html, nothing } from "lit";
import { ActiveFiltersBadges } from "./active-filters-badges";
import { translate } from "../../../../localization";
import { formatShowFilterTitle, normalizeDeletedFilterBooleanValue, normalizeEmptyFilterValue } from "../../utils";

const MAX_VISIBLE_CHIPS = 5;

const renderChip = (id, filter, option, props) => html`
  <dss-chip
    label=${normalizeEmptyFilterValue(option.title, filter.title)}
    size="sm"
    hasdelete
    selected
    @click=${() => props._onDeleteFilterValue(id, filter.id, option.id, false)}
    @onDelete=${() => props._onDeleteFilterValue(id, filter.id, option.id, false)}
  ></dss-chip>
`;

const renderCommonChip = (filter, props) => html`
  <dss-chip
    label=${formatShowFilterTitle(filter.title)}
    size="sm"
    hasdelete
    selected
    @click=${() => props._onDeleteCommonFilterValue(filter.id)}
    @onDelete=${() => props._onDeleteCommonFilterValue(filter.id)}
  ></dss-chip>
`;

const renderDiagnosticChip = (id, value, props) => html`
  <dss-chip
    label=${value}
    size="sm"
    hasdelete
    selected
    @click=${() => props._onDeleteDiagnosticFilterValue(id)}
    @onDelete=${() => props._onDeleteDiagnosticFilterValue(id)}
  ></dss-chip>
`;

const renderUpChip = (id, value, props) => html`
  <dss-chip
    label=${value}
    size="sm"
    hasdelete
    selected
    @click=${() => props._onDeleteUpFilterValue(id)}
    @onDelete=${() => props._onDeleteUpFilterValue(id)}
  ></dss-chip>
`;

const renderFilterTitle = (title) => html`<div class="filter-title">${`${title}:`}</div>`;

export const template = (props: ActiveFiltersBadges) => {
  //TODO: refactor chips render
  if (
    props.enabledFilters.length === 0 &&
    props.enabledCommonFilters.length === 0 &&
    props.diagnosticFilterEnabled?.enabledValues?.length === 0 &&
    props.upFilterEnabled.enabledValues.length === 0
  )
    return nothing;

  let visibleCount = 0;
  const visibleChips: any[] = [];
  const hiddenElements: any[] = [];
  const hiddenChips: any[] = [];

  const diagnostic = props.diagnosticFilterEnabled;

  if (diagnostic) {
    const { title, enabledValues } = diagnostic;

    if (enabledValues && enabledValues.length > 0) {
      let titleAddedToVisible = false;
      let titleAddedToHidden = false;

      // Només mostrem els diagnòstics pare (no les evolucions ni relacions)
      for (const diagnosticItem of enabledValues) {
        const chip = renderDiagnosticChip(diagnosticItem.codi, diagnosticItem.descripcio, props);

        if (visibleCount < MAX_VISIBLE_CHIPS) {
          if (!titleAddedToVisible) {
            visibleChips.push(renderFilterTitle(title));
            titleAddedToVisible = true;
          }
          visibleChips.push(chip);
          visibleCount++;
        } else {
          if (!titleAddedToHidden) {
            hiddenElements.push(renderFilterTitle(title));
            titleAddedToHidden = true;
          }
          hiddenElements.push(chip);
          hiddenChips.push(chip);
        }
      }
    }
  }

  const up = props.upFilterEnabled;

  if (up) {
    const { title, values, enabledValues } = up;
    const selectedOptions = values.filter((opt) => enabledValues.includes(opt.value));

    if (selectedOptions.length > 0) {
      let titleAddedToVisible = false;
      let titleAddedToHidden = false;

      for (const option of selectedOptions) {
        const chip = renderUpChip(option.value, option.label, props);

        if (visibleCount < MAX_VISIBLE_CHIPS) {
          if (!titleAddedToVisible) {
            visibleChips.push(renderFilterTitle(title));
            titleAddedToVisible = true;
          }
          visibleChips.push(chip);
          visibleCount++;
        } else {
          if (!titleAddedToHidden) {
            hiddenElements.push(renderFilterTitle(title));
            titleAddedToHidden = true;
          }
          hiddenElements.push(chip);
          hiddenChips.push(chip);
        }
      }
    }
  }

  for (const filter of props.enabledCommonFilters) {
    const chip = renderCommonChip(filter, props);

    if (visibleCount < MAX_VISIBLE_CHIPS) {
      visibleChips.push(chip);
      visibleCount++;
    } else {
      hiddenElements.push(chip);
      hiddenChips.push(chip);
    }
  }

  for (const { title, id, filters } of props.enabledFilters) {
    let titleAddedToVisible = false;
    let titleAddedToHidden = false;

    for (const filter of filters) {
      for (const value of [...new Set(filter.enabledValues)]) {
        let filterOption = filter?.options?.find((o) => o.id === value);

        if (filterOption) {
          //Fill when filter option doesnt have title (description)
          filterOption.title = filterOption.title !== "" ? filterOption.title : value;
        } else {
          //Hi ha un filtre de curs clinic "veure eliminats" booleà que no serà un filterOption, per tant entra aquí
          //Aqui es crea el objecte que es passa al chip
          const deletedFiltersOption = normalizeDeletedFilterBooleanValue(value);
          filterOption = { title: deletedFiltersOption, id: deletedFiltersOption };
        }

        //const chip2 = renderChip(id, filter, normalizeDeletedFilterBooleanValue(value), props);

        const chip = renderChip(id, filter, filterOption, props);

        if (visibleCount < MAX_VISIBLE_CHIPS) {
          if (!titleAddedToVisible) {
            visibleChips.push(renderFilterTitle(title));
            titleAddedToVisible = true;
          }
          visibleChips.push(chip);
          visibleCount++;
        } else {
          if (!titleAddedToHidden) {
            hiddenElements.push(renderFilterTitle(title));
            titleAddedToHidden = true;
          }
          hiddenElements.push(chip);
          hiddenChips.push(chip);
        }
      }
    }
  }

  return html`
  <div class="filters-container">
    <div class="filters-header">${translate("chipHeader.selection")}</div>
    <div class="filter-groups">
      ${visibleChips}
      ${
        hiddenChips.length > 0
          ? html`
            <div>
              <dss-button label="+ ${hiddenChips.length}" size="sm" variant="primary"></dss-button>
              <dss-popover title=${translate("chipHeader.selection")} position="bottom" popoverFixed>
                <dss-popover-body slot="body">
                  <div class="popover-content">
                    ${hiddenElements}
                  </div>
                </dss-popover-body>
              </dss-popover>
            </div>
          `
          : nothing
      }
    </div>
  </div>
  <div class="clean-filters">
    <div class="separator">|</div>
    <dss-button variant="secondary" size="sm" label=${translate("chipHeader.cleanFilters")} icon="cleaning_services" @click=${props.resetFilters}></dss-button>
  </div>
  `;
};
